/**
 * Published listing (active | paused | closed) — sectioned view (Change 20, AC-6 / AC-7).
 *
 * One section at a time through the existing hash-addressed DetailTabs: Overview (default),
 * Pipeline, Showings, Marketing (listings activated at least once, listing-marketing-hub BR-1),
 * Screening & details, Contact & agent. Inactive sections are unmounted, so anything that must
 * survive a switch lives in the page shell or in react-query.
 *
 * The Overview carries a pipeline summary, the intake email and public link with copy actions,
 * and one settings card per setup item; each card's Edit opens the existing editor in a
 * SettingEditSheet, and the card updates in place from the shared queries after a save.
 */

import { useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DetailTabs, TabsContent } from '../../../components/layout/DetailTabs';
import { MarketingTab } from '../../../components/leasing/marketing/MarketingTab';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { JurisdictionCoverageStatement } from '../../../components/JurisdictionCoverageStatement';
import type { Listing, ScreeningCriteria } from '../../../lib/leasing.api';
import { AgentPolicyEditor } from './AgentPolicyEditor';
import { ContactInfoEditor } from './ContactInfoEditor';
import { ListingFieldsEditor } from './ListingFieldsEditor';
import {
  PIPELINE_STAGES,
  PipelineBoard,
  STAGE_LABELS_PIPELINE,
  listingLeadsQueryOptions,
} from './PipelineBoard';
import { ScreeningCriteriaEditor } from './ScreeningCriteriaEditor';
import { useListingConstraints } from './shared';
import { useScrollToMarketingTab } from './useScrollToMarketingTab';
import { SettingEditSheet } from './SettingEditSheet';
import { CopyValueBox } from './ShareCopyButtons';
import { ShowingsSection } from './ShowingsSection';
import { ShowingsSetup } from './showings/ShowingsSetup';
import { useGuideState } from '../listing-guide/useGuideState';
import { describePolicySource, useListingAgentPolicy } from './useListingAgentPolicy';

export type ListingSectionKey = 'overview' | 'pipeline' | 'showings' | 'marketing' | 'screening' | 'contact-agent';

const SECTION_TABS: { key: ListingSectionKey; label: string; testId: string }[] = [
  { key: 'overview', label: 'Overview', testId: 'listing-section-tab-overview' },
  { key: 'pipeline', label: 'Pipeline', testId: 'listing-section-tab-pipeline' },
  { key: 'showings', label: 'Showings', testId: 'listing-section-tab-showings' },
  { key: 'marketing', label: 'Marketing', testId: 'listing-section-tab-marketing' },
  { key: 'screening', label: 'Screening & details', testId: 'listing-section-tab-screening' },
  { key: 'contact-agent', label: 'Contact & agent', testId: 'listing-section-tab-contact-agent' },
];

interface ListingSectionsProps {
  listing: Listing;
  screeningCriteria: ScreeningCriteria | null;
  intakeAddress: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactInfoError: string | null;
  canManage: boolean;
  /** DC-51: the leasing agent applies to this listing (current paid period or agent add-on). */
  leasingAgentAvailable: boolean;
  onSaved: () => void;
  /** Re-activates the paused listing (the page's Re-activate action), offered on the Marketing tab. */
  onReactivate: () => void;
}

export function ListingSections(props: ListingSectionsProps) {
  const { listing, screeningCriteria, intakeAddress, contactName, contactPhone, contactInfoError, canManage } = props;
  const sectionsRef = useRef<HTMLDivElement>(null);

  useScrollToMarketingTab(sectionsRef);
  const isClosed = listing.status === 'closed';
  const coverage = useListingConstraints(listing)?.jurisdiction.coverage;
  // BR-1: the Marketing tab is offered once the listing has been activated at least once.
  const everActivated = listing.activatedAt !== null;
  const tabs = useMemo(
    () => (everActivated ? SECTION_TABS : SECTION_TABS.filter((t) => t.key !== 'marketing')),
    [everActivated],
  );

  return (
    <div ref={sectionsRef} className="min-w-0 scroll-mt-16">
      <DetailTabs<ListingSectionKey> tabs={tabs} defaultTab="overview" className="min-w-0">
        <TabsContent value="overview" className="mt-5" data-testid="listing-section-overview">
          <OverviewSection {...props} />
        </TabsContent>
        <TabsContent value="pipeline" className="mt-5" data-testid="listing-section-pipeline">
          <PipelineBoard listingId={listing.id} />
        </TabsContent>
        <TabsContent value="showings" className="mt-5" data-testid="listing-section-showings">
          <ShowingsSection listingId={listing.id} isClosed={isClosed} listing={listing} />
          <section data-testid="availability-section" className="mb-6">
            <ShowingsSetup listing={listing} />
          </section>
        </TabsContent>
        {everActivated && (
          <TabsContent value="marketing" className="mt-5" data-testid="listing-section-marketing">
            <MarketingTab listing={listing} intakeAddress={intakeAddress} onReactivate={props.onReactivate} />
          </TabsContent>
        )}
        <TabsContent value="screening" className="mt-5" data-testid="listing-section-screening">
          {/* Forms opt back into a capped width inside the full-width page shell. */}
          <div className="max-w-3xl">
            <div className="mb-4">
              <JurisdictionCoverageStatement coverage={coverage} subject="listing" />
            </div>
            <ScreeningCriteriaEditor listing={listing} screeningCriteria={screeningCriteria} onUpdated={props.onSaved} />
            <ListingFieldsEditor listing={listing} onUpdated={props.onSaved} />
          </div>
        </TabsContent>
        <TabsContent value="contact-agent" className="mt-5" data-testid="listing-section-contact-agent">
          <div className="max-w-3xl">
            <ContactInfoEditor
              listing={listing}
              intakeAddress={intakeAddress}
              contactPhone={contactPhone}
              contactName={contactName}
              activationError={contactInfoError}
              onSaved={props.onSaved}
            />
            {props.leasingAgentAvailable && (
              <section
                aria-labelledby="listing-agent-policy-title"
                className="mb-6 rounded-lg border border-border bg-card p-5"
              >
                <h2 id="listing-agent-policy-title" className="mb-1 text-base font-semibold">
                  AI leasing agent
                </h2>
                <AgentPolicyEditor listingId={listing.id} canManage={canManage} />
              </section>
            )}
          </div>
        </TabsContent>
      </DetailTabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

type SettingKey = 'contact' | 'screening' | 'showing' | 'agent';

const SHEET_TITLES: Record<SettingKey, string> = {
  contact: 'Contact info',
  screening: 'Screening criteria',
  showing: 'Showing policy & availability',
  agent: 'AI leasing agent',
};

function OverviewSection({
  listing,
  screeningCriteria,
  intakeAddress,
  contactName,
  contactPhone,
  contactInfoError,
  canManage,
  leasingAgentAvailable,
  onSaved,
}: ListingSectionsProps) {
  // The same saved-data summaries the guided setup shows (one derivation, no second copy).
  const guide = useGuideState({ unitId: listing.unitId, listing });
  const { data: agentPolicy } = useListingAgentPolicy(listing.id, canManage);
  const [editing, setEditing] = useState<SettingKey | null>(null);
  // The setting the sheet shows — kept after close so the closing sheet keeps its content and its
  // focus-return target (the sheet stays mounted; only `open` toggles).
  const [sheetFor, setSheetFor] = useState<SettingKey>('contact');
  const editRefs: Record<SettingKey, RefObject<HTMLButtonElement>> = {
    contact: useRef<HTMLButtonElement>(null),
    screening: useRef<HTMLButtonElement>(null),
    showing: useRef<HTMLButtonElement>(null),
    agent: useRef<HTMLButtonElement>(null),
  };
  const isClosed = listing.status === 'closed';
  const canEdit = canManage && !isClosed;
  const closeSheet = () => setEditing(null);

  const cards: { key: SettingKey; value: string; note: string }[] = [
    {
      key: 'contact',
      value: guide.done.contact ? guide.steps.contact.summary : 'Not set',
      note: 'Shown on the public listing.',
    },
    { key: 'screening', value: guide.screening.summary, note: 'Used when you review applicants.' },
    {
      key: 'showing',
      value: guide.steps.showings.summary,
      note: listing.prospectBookingMode === 'direct' ? 'Prospects book directly.' : 'You approve each request.',
    },
  ];
  // AC-6: the AI agent card is always one of the four settings. Without a current paid period or
  // the agent add-on the saved choice is still editable; the note says when it starts to apply.
  const agentSource = agentPolicy ? describePolicySource(agentPolicy.enablementSource) : null;
  cards.push({
    key: 'agent',
    value: guide.steps.agent.summary,
    note: leasingAgentAvailable
      ? agentSource
        ? `${capitalize(agentSource)}.`
        : ''
      : 'Applies once this listing has a current paid period or the agent add-on.',
  });

  const sheetBody = (key: SettingKey): ReactNode => {
    switch (key) {
      case 'contact':
        return (
          <ContactInfoEditor
            variant="bare"
            listing={listing}
            intakeAddress={intakeAddress}
            contactPhone={contactPhone}
            contactName={contactName}
            activationError={contactInfoError}
            onSaved={onSaved}
            onDone={closeSheet}
          />
        );
      case 'screening':
        return (
          <ScreeningCriteriaEditor
            variant="bare"
            listing={listing}
            screeningCriteria={screeningCriteria}
            onUpdated={onSaved}
            onDone={closeSheet}
          />
        );
      case 'showing':
        return (
          <>
            <ShowingsSetup listing={listing} />
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                className="min-h-[44px]"
                data-testid="listing-setting-sheet-showing-done"
                onClick={closeSheet}
              >
                Done
              </Button>
            </div>
          </>
        );
      case 'agent':
        return <AgentPolicyEditor listingId={listing.id} canManage={canManage} onConfirmed={closeSheet} />;
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
      <div className="flex min-w-0 flex-col gap-6">
        <PipelineSummary listingId={listing.id} />

        <section aria-labelledby="listing-settings-title" className="flex flex-col gap-3">
          <h2 id="listing-settings-title" className="text-base font-semibold text-foreground">
            Listing settings
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {cards.map((card) => (
              <div
                key={card.key}
                data-testid={`listing-setting-card-${card.key}`}
                className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-border bg-card p-4"
              >
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground">{SHEET_TITLES[card.key]}</h3>
                  {canEdit && (
                    <Button
                      ref={editRefs[card.key]}
                      type="button"
                      variant="ghost"
                      className="min-h-[44px] -mr-2 px-3 text-primary"
                      data-testid={`listing-setting-card-${card.key}-edit`}
                      aria-label={`Edit ${SHEET_TITLES[card.key].toLowerCase()}`}
                      onClick={() => {
                        setSheetFor(card.key);
                        setEditing(card.key);
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </div>
                <p className="text-sm text-foreground break-words" data-testid={`listing-setting-card-${card.key}-value`}>
                  {card.value}
                </p>
                {card.note && <p className="text-xs text-muted-foreground">{card.note}</p>}
              </div>
            ))}
          </div>
        </section>
      </div>

      <aside className="flex min-w-0 flex-col gap-4" aria-label="Share this listing">
        {!isClosed && (
          <div className="rounded-lg border border-border bg-card p-4">
            <CopyValueBox
              label="Intake email"
              value={intakeAddress}
              emptyText="Address pending…"
              testIdPrefix="overview-intake-email"
              caption={
                listing.status === 'paused'
                  ? 'Paused — new inbound will receive your on-hold auto-reply.'
                  : 'Use this as your contact email on rental listing sites or your own listing.'
              }
            />
          </div>
        )}
        {listing.publicUrl && (
          <div className="rounded-lg border border-border bg-card p-4">
            <CopyValueBox
              label="Public link"
              value={listing.publicUrl}
              emptyText="Link pending…"
              testIdPrefix="overview-public-link"
            />
          </div>
        )}
      </aside>

      <SettingEditSheet
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) closeSheet();
        }}
        title={SHEET_TITLES[sheetFor]}
        returnFocusRef={editRefs[sheetFor]}
        testId={`listing-setting-sheet-${sheetFor}`}
        size={sheetFor === 'showing' ? 'wide' : 'default'}
      >
        {sheetBody(sheetFor)}
      </SettingEditSheet>
    </div>
  );
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ---------------------------------------------------------------------------
// Pipeline summary (Overview)
// ---------------------------------------------------------------------------

function PipelineSummary({ listingId }: { listingId: string }) {
  // Same first-page (100) query the Pipeline board reads; exact for typical listings, and the
  // total says "100+" when there are more than the page holds.
  const { data, isLoading, error } = useQuery(listingLeadsQueryOptions(listingId));
  const leads = data?.items ?? [];
  const total = data?.totalCount ?? 0;
  const counts = new Map(PIPELINE_STAGES.map((stage) => [stage, 0]));
  for (const lead of leads) {
    const current = counts.get(lead.stage);
    if (current !== undefined) counts.set(lead.stage, current + 1);
  }

  return (
    <section
      aria-labelledby="listing-pipeline-summary-title"
      data-testid="listing-pipeline-summary"
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 id="listing-pipeline-summary-title" className="text-base font-semibold text-foreground">
          Pipeline
          {data && (
            <span className="ml-2 text-sm font-normal text-muted-foreground" data-testid="listing-pipeline-summary-total">
              {total > 100 ? '100+' : total} lead{total === 1 ? '' : 's'}
            </span>
          )}
        </h2>
        <a
          href="#pipeline"
          data-testid="listing-pipeline-summary-open"
          className="inline-flex min-h-[44px] items-center px-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Open pipeline
        </a>
      </div>
      {isLoading && <Skeleton className="h-16 w-full" />}
      {error && (
        <p className="text-sm text-destructive" role="alert" data-testid="listing-pipeline-summary-error">
          {error instanceof Error ? error.message : 'Failed to load the pipeline'}
        </p>
      )}
      {data && (
        <>
          <dl className="grid grid-cols-2 gap-2 min-[480px]:grid-cols-3 sm:grid-cols-5">
            {PIPELINE_STAGES.map((stage) => (
              <div key={stage} className="rounded-md bg-muted px-3 py-2" data-testid={`listing-pipeline-summary-${stage}`}>
                <dt className="text-xs text-muted-foreground">{STAGE_LABELS_PIPELINE[stage]}</dt>
                <dd className="text-xl font-semibold text-foreground">{counts.get(stage) ?? 0}</dd>
              </div>
            ))}
          </dl>
          {total === 0 && (
            <p className="text-sm text-muted-foreground">
              No leads yet. They will appear here as prospects write to your intake email.
            </p>
          )}
        </>
      )}
    </section>
  );
}
